import { PencilIcon } from "@heroicons/react/outline";
import { KeyboardEvent, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { Signal, useEditSignal } from "@/queries/signals";

interface RenameSignalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  signal: Signal | null;
}

const RenameSignalDialog = ({ open, onOpenChange, signal }: RenameSignalDialogProps) => {
  const [signalName, setSignalName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const userToken = useAuth()?.userToken ?? "";
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;
  const editSignal = useEditSignal();

  useEffect(() => {
    let animationFrameId: number | undefined;

    if (open && signal) {
      setSignalName(signal.name);
      animationFrameId = window.requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [open, signal]);

  const isNameChanged = signal ? signalName.trim() !== signal.name : false;
  const canRename = Boolean(signalName.trim() && isNameChanged && !editSignal.isPending);

  const handleRename = async () => {
    if (!signal || !canRename) {
      return;
    }

    await editSignal.mutateAsync({
      workspaceId,
      signalId: signal.id,
      userToken,
      name: signalName.trim(),
      successMessage: "Signal renamed",
    });
    onOpenChange(false);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void handleRename();
    }
  };

  return (
    <Dialog open={open && !!signal} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-medium">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
              <PencilIcon className="h-4 w-4 text-blue-600" />
            </div>
            <span className="truncate">Rename signal</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-hidden">
          {signal && (
            <div className="overflow-hidden rounded-lg bg-gray-50 p-3">
              <OverflowDisplayTooltip
                content={signal.name}
                className="block w-full text-sm font-medium text-gray-900"
                styles={{ display: "block", width: "100%" }}
              >
                {signal.name}
              </OverflowDisplayTooltip>
              <p className="text-xs text-gray-500">Signal</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="signal-name">New Name</Label>
            <Input
              id="signal-name"
              type="text"
              ref={inputRef}
              value={signalName}
              onChange={event => setSignalName(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter signal name..."
              className="w-full"
              disabled={editSignal.isPending}
            />
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={editSignal.isPending}
          >
            Cancel
          </Button>
          <Button onClick={() => void handleRename()} disabled={!canRename}>
            {editSignal.isPending ? "Renaming..." : "Rename signal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RenameSignalDialog;
